import {
  EdgelessIcon,
  LinkedEdgelessIcon,
  LinkedPageIcon,
  PageIcon,
} from '@blocksuite/icons/rc';
import {
  type DropTargetDropEvent,
  Loading,
  toast,
  Tooltip,
} from '@pique/component';
import { InfoModal } from '@pique/core/components/pique/page-properties';
import { useAsyncCallback } from '@pique/core/hooks/pique-async-hooks';
import { DocsSearchService } from '@pique/core/modules/docs-search';
import type { AffineDNDData, CanDropArgs } from '@pique/core/types/dnd';
import { useI18n } from '@pique/i18n';
import {
  DocsService,
  GlobalContextService,
  LiveData,
  useLiveData,
  useServices,
} from '@toeverything/infra';
import { useCallback, useLayoutEffect, useMemo, useState } from 'react';

import { ExplorerTreeNode, type ExplorerTreeNodeDropEffect } from '../../tree';
import type { GenericExplorerNode } from '../types';
import { Empty } from './empty';
import { useExplorerDocNodeOperations } from './operations';
import * as styles from './styles.css';

export const ExplorerDocNode = ({
  docId,
  onDrop,
  location,
  reorderable,
  isLinked,
  canDrop,
  operations: additionalOperations,
  dropEffect,
}: {
  docId: string;
  isLinked?: boolean;
} & GenericExplorerNode) => {
  const t = useI18n();
  const { docsSearchService, docsService, globalContextService } = useServices({
    DocsSearchService,
    DocsService,
    GlobalContextService,
  });
  const active =
    useLiveData(globalContextService.globalContext.docId.$) === docId;
  const [collapsed, setCollapsed] = useState(true);

  const docRecord = useLiveData(docsService.list.doc$(docId));
  const docPrimaryMode = useLiveData(docRecord?.primaryMode$);
  const docTitle = useLiveData(docRecord?.title$);
  const isInTrash = useLiveData(docRecord?.trash$);

  const Icon = useCallback(
    ({ className }: { className?: string }) => {
      return isLinked ? (
        docPrimaryMode === 'edgeless' ? (
          <LinkedEdgelessIcon className={className} />
        ) : (
          <LinkedPageIcon className={className} />
        )
      ) : docPrimaryMode === 'edgeless' ? (
        <EdgelessIcon className={className} />
      ) : (
        <PageIcon className={className} />
      );
    },
    [docPrimaryMode, isLinked]
  );

  const children = useLiveData(
    useMemo(
      () => LiveData.from(docsSearchService.watchRefsFrom(docId), null),
      [docsSearchService, docId]
    )
  );

  const indexerLoading = useLiveData(
    docsSearchService.indexer.status$.map(
      v => v.remaining === undefined || v.remaining > 0
    )
  );
  const [referencesLoading, setReferencesLoading] = useState(true);
  useLayoutEffect(() => {
    setReferencesLoading(
      prev =>
        prev &&
        indexerLoading /* after loading becomes false, it never becomes true */
    );
  }, [indexerLoading]);

  const dndData = useMemo(() => {
    return {
      draggable: {
        entity: {
          type: 'doc',
          id: docId,
        },
        from: location,
      },
      dropTarget: {
        at: 'explorer:doc',
      },
    } satisfies AffineDNDData;
  }, [docId, location]);

  const handleRename = useAsyncCallback(
    async (newName: string) => {
      await docsService.changeDocTitle(docId, newName);
    },
    [docId, docsService]
  );

  const handleDropOnDoc = useAsyncCallback(
    async (data: DropTargetDropEvent<AffineDNDData>) => {
      if (data.treeInstruction?.type === 'make-child') {
        if (data.source.data.entity?.type === 'doc') {
          await docsService.addLinkedDoc(docId, data.source.data.entity.id);
        } else {
          toast(t['com.pique.rootAppSidebar.doc.link-doc-only']());
        }
      } else {
        onDrop?.(data);
      }
    },
    [docId, docsService, onDrop, t]
  );

  const handleDropEffectOnDoc = useCallback<ExplorerTreeNodeDropEffect>(
    data => {
      if (data.treeInstruction?.type === 'make-child') {
        if (data.source.data.entity?.type === 'doc') {
          return 'link';
        }
      } else {
        return dropEffect?.(data);
      }
      return;
    },
    [dropEffect]
  );

  const handleDropOnPlaceholder = useAsyncCallback(
    async (data: DropTargetDropEvent<AffineDNDData>) => {
      if (data.source.data.entity?.type === 'doc') {
        // TODO(eyhn): timeout&error handling
        await docsService.addLinkedDoc(docId, data.source.data.entity.id);
      } else {
        toast(t['com.pique.rootAppSidebar.doc.link-doc-only']());
      }
    },
    [docId, docsService, t]
  );

  const handleCanDrop = useCallback(
    (args: CanDropArgs) => {
      const entityType = args.source.data.entity?.type;
      return args.treeInstruction?.type !== 'make-child'
        ? ((typeof canDrop === 'function' ? canDrop(args) : canDrop) ?? true)
        : entityType === 'doc';
    },
    [canDrop]
  );

  const [enableInfoModal, setEnableInfoModal] = useState(false);
  const operations = useExplorerDocNodeOperations(
    docId,
    useMemo(
      () => ({
        openInfoModal: () => setEnableInfoModal(true),
        openNodeCollapsed: () => setCollapsed(false),
      }),
      []
    )
  );

  const finalOperations = useMemo(() => {
    if (additionalOperations) {
      return [...operations, ...additionalOperations];
    }
    return operations;
  }, [additionalOperations, operations]);

  if (isInTrash || !docRecord) {
    return null;
  }

  return (
    <>
      <ExplorerTreeNode
        icon={Icon}
        name={docTitle || t['Untitled']()}
        dndData={dndData}
        onDrop={handleDropOnDoc}
        renameable
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        canDrop={handleCanDrop}
        to={`/${docId}`}
        active={active}
        postfix={
          referencesLoading &&
          !collapsed && (
            <Tooltip
              content={t['com.pique.rootAppSidebar.docs.references-loading']()}
            >
              <div className={styles.loadingIcon}>
                <Loading />
              </div>
            </Tooltip>
          )
        }
        reorderable={reorderable}
        onRename={handleRename}
        childrenPlaceholder={<Empty onDrop={handleDropOnPlaceholder} />}
        operations={finalOperations}
        dropEffect={handleDropEffectOnDoc}
        data-testid={`explorer-doc-${docId}`}
      >
        {children?.map(child => (
          <ExplorerDocNode
            key={child.docId}
            docId={child.docId}
            reorderable={false}
            location={{
              at: 'explorer:doc:linked-docs',
              docId,
            }}
            isLinked
          />
        ))}
      </ExplorerTreeNode>
      {enableInfoModal && (
        <InfoModal
          open={enableInfoModal}
          onOpenChange={setEnableInfoModal}
          docId={docId}
        />
      )}
    </>
  );
};
